'use client'

import * as React from 'react'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'

interface PromptInputContextValue {
  onSubmit: () => void
  isSubmitting?: boolean
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
}

const PromptInputContext = React.createContext<PromptInputContextValue | undefined>(undefined)

interface PromptInputProps extends React.HTMLAttributes<HTMLDivElement> {
  onSubmit?: () => void
  isLoading?: boolean
}

const PromptInput = React.forwardRef<HTMLDivElement, PromptInputProps>(
  ({ children, onSubmit, isLoading, className, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null)

    const contextValue = React.useMemo(() => ({
      onSubmit: () => onSubmit?.(),
      isSubmitting: isLoading,
      textareaRef
    }), [onSubmit, isLoading])

    return (
      <PromptInputContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(
            "flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-1.5 sm:px-4 sm:py-2 shadow-sm transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </PromptInputContext.Provider>
    )
  }
)
PromptInput.displayName = "PromptInput"

const PromptInputTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, onKeyDown, value, ...props }, ref) => {
    const context = React.useContext(PromptInputContext)
    const textareaRef = context?.textareaRef || React.useRef<HTMLTextAreaElement>(null)
    const isMobile = useIsMobile()
    
    // Handle external ref if provided
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // On desktop: Enter sends, Shift+Enter new line
      // On mobile: Enter always new line, Send button for sending
      if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
        e.preventDefault()
        context?.onSubmit()
      }
      onKeyDown?.(e)
    }

    // Auto-resize without causing layout shift
    React.useLayoutEffect(() => {
      const textarea = textareaRef.current
      if (textarea) {
        // Reset height to measure correctly, but do it in a single frame
        textarea.style.height = '0'
        const newHeight = Math.min(textarea.scrollHeight, 200)
        textarea.style.height = `${newHeight}px`
      }
    }, [value])

    return (
      <Textarea
        ref={textareaRef}
        value={value}
        rows={1}
        className={cn(
          "min-h-[24px] max-h-[200px] flex-1 resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/50",
          className
        )}
        onKeyDown={handleKeyDown}
        {...props}
      />
    )
  }
)
PromptInputTextarea.displayName = "PromptInputTextarea"

const PromptInputToolbar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-between gap-2 mt-1.5 sm:mt-2", className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
PromptInputToolbar.displayName = "PromptInputToolbar"

const PromptInputActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-2", className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
PromptInputActions.displayName = "PromptInputActions"

interface PromptInputSubmitProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

const PromptInputSubmit = React.forwardRef<HTMLButtonElement, PromptInputSubmitProps>(
  ({ className, onClick, disabled, ...props }, ref) => {
    const context = React.useContext(PromptInputContext)
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      context?.onSubmit()
      onClick?.(e)
    }

    return (
      <Button
        ref={ref}
        size="icon"
        className={cn("h-8 w-8 rounded-lg", className)}
        onClick={handleClick}
        disabled={disabled || context?.isSubmitting}
        {...props}
      >
        <Send className="h-4 w-4" />
        <span className="sr-only">Send</span>
      </Button>
    )
  }
)
PromptInputSubmit.displayName = "PromptInputSubmit"

export {
  PromptInput,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputActions,
  PromptInputSubmit
}
